export const adminThemes = ["Nature", "History", "Food", "Adventure", "Sea", "Culture"];

export const adPlacements = [
  { value: "home", label: "Keşfet (Ana sayfa)" },
  { value: "map", label: "Karadeniz (Harita)" },
  { value: "deals", label: "Fırsatlar" },
  { value: "businesses", label: "İşletmeler" },
  { value: "all", label: "Tüm ekranlar" },
];

export const provinceIds = [
  "artvin",
  "rize",
  "trabzon",
  "giresun",
  "ordu",
  "samsun",
  "sinop",
  "kastamonu",
  "bartin",
  "karabuk",
  "zonguldak",
  "duzce",
  "bolu",
  "amasya",
  "tokat",
  "gumushane",
  "bayburt",
  "corum",
];

export const businessCategories = ["hotel", "resort", "pension", "restaurant", "souvenir", "transport", "agency", "guide"];
export const bookingStatuses = ["pending", "confirmed", "cancelled"];
export const subscriptionStatuses = ["active", "cancelled", "expired"];
export const paymentPlanIds = ["basic-monthly", "pro-monthly", "pro-yearly"];
export const userRoles = ["customer", "business", "agency", "guide", "admin"];
export const supportRequestCategories = ["account", "technical", "booking", "privacy", "feedback", "other"];
export const supportRequestStatuses = ["open", "in_progress", "resolved", "closed"];

function asArray(value) {
  return Array.isArray(value) ? value : [];
}

function asString(value, fallback = "") {
  return typeof value === "string" ? value : fallback;
}

function normalizeDate(value) {
  return asString(value, "");
}

function uniqueStrings(values, fallback = []) {
  const next = asArray(values)
    .map((value) => (typeof value === "string" ? value.trim() : ""))
    .filter(Boolean);

  return next.length ? Array.from(new Set(next)) : fallback;
}

function normalizeHighlight(item, fallbackPrefix) {
  const now = new Date().toISOString();
  const raw = typeof item === "string" ? { title: item } : item || {};

  return {
    id: asString(raw.id, `${fallbackPrefix}-${Math.random().toString(16).slice(2, 8)}`),
    title: asString(raw.title),
    shortDescription: asString(raw.shortDescription),
    theme: asString(raw.theme, "Nature"),
    locationHint: asString(raw.locationHint),
    bestSeason: asString(raw.bestSeason),
    imageUrl: raw.imageUrl ? asString(raw.imageUrl) : null,
    createdAt: normalizeDate(raw.createdAt || now),
  };
}

export function normalizeProvince(input = {}) {
  return {
    id: asString(input.id),
    name: asString(input.name),
    slogan: asString(input.slogan),
    story: asString(input.story),
    history: asString(input.history),
    heroImage: input.heroImage || null,
    historyCoverImage: input.historyCoverImage || null,
    landmark: asString(input.landmark),
    foods: uniqueStrings(input.foods),
    themes: uniqueStrings(input.themes),
    highlights: asArray(input.highlights).map((item, index) => normalizeHighlight(item, `hl-${input.id || index}`)),
    experiences: asArray(input.experiences).map((item, index) => normalizeHighlight(item, `exp-${input.id || index}`)),
    seasons: uniqueStrings(input.seasons, ["Spring", "Summer", "Autumn", "Winter"]),
    createdAt: normalizeDate(input.createdAt),
    updatedAt: normalizeDate(input.updatedAt),
    deletedAt: input.deletedAt || null,
  };
}

export function normalizeDeal(input = {}) {
  return {
    id: asString(input.id),
    provinceId: asString(input.provinceId),
    title: asString(input.title),
    description: asString(input.description),
    priceText: asString(input.priceText),
    startDate: asString(input.startDate),
    endDate: asString(input.endDate),
    theme: asString(input.theme, "Nature"),
    contactCta: asString(input.contactCta),
    createdAt: normalizeDate(input.createdAt),
    updatedAt: normalizeDate(input.updatedAt),
    deletedAt: input.deletedAt || null,
  };
}

export function normalizeAd(input = {}) {
  const placement = asString(input.placement, "home");
  const allowed = adPlacements.map((item) => item.value);

  return {
    id: asString(input.id),
    title: asString(input.title),
    subtitle: asString(input.subtitle),
    imageUrl: input.imageUrl ? asString(input.imageUrl) : null,
    backgroundColor: asString(input.backgroundColor, "#1B4332"),
    linkUrl: asString(input.linkUrl),
    placement: allowed.includes(placement) ? placement : "home",
    sortOrder: Number.isFinite(Number(input.sortOrder)) ? Number(input.sortOrder) : 0,
    isActive: input.isActive !== false,
    createdAt: normalizeDate(input.createdAt),
    updatedAt: normalizeDate(input.updatedAt),
    deletedAt: input.deletedAt || null,
  };
}

function normalizeBusinessCategory(category) {
  switch (category) {
    case "tour_agency":
      return "agency";
    case "cafe":
      return "restaurant";
    case "museum":
      return "guide";
    default:
      return businessCategories.includes(category) ? category : "hotel";
  }
}

export function normalizeBusiness(input = {}) {
  const location = input.location || {};
  const contact = input.contact || {};
  const provinceId = asString(location.provinceId || input.provinceId);

  return {
    id: asString(input.id),
    name: asString(input.name),
    category: normalizeBusinessCategory(asString(input.category)),
    location: {
      provinceId,
      district: asString(location.district),
      town: asString(location.town),
      addressLine: asString(location.addressLine || input.address),
    },
    contact: {
      phone: asString(contact.phone || input.phone),
      email: asString(contact.email || input.email),
      website: asString(contact.website || input.website),
      whatsapp: asString(contact.whatsapp || input.whatsapp),
    },
    description: asString(input.description),
    priceFromText: asString(input.priceFromText || input.priceText || input.priceRange),
    imageUrl: input.imageUrl || input.image || null,
    ownerUserId: asString(input.ownerUserId),
    updatedAt: normalizeDate(input.updatedAt),
    createdAt: normalizeDate(input.createdAt),
    deletedAt: input.deletedAt || null,
  };
}

export function normalizeBooking(input = {}) {
  const customerName = asString(input.customerName || input.customerDisplayName || input.guestName);

  return {
    id: asString(input.id),
    businessId: asString(input.businessId),
    customerUserId: asString(input.customerUserId),
    customerDisplayName: asString(input.customerDisplayName),
    customerName,
    startDate: asString(input.startDate || input.date),
    nights: Number(input.nights || 1),
    notes: asString(input.notes),
    createdAt: normalizeDate(input.createdAt),
    status: bookingStatuses.includes(input.status) ? input.status : "pending",
    deletedAt: input.deletedAt || null,
  };
}

export function normalizeSubscription(input = {}) {
  return {
    id: asString(input.id || input.userId),
    userId: asString(input.userId),
    planId: paymentPlanIds.includes(input.planId) ? input.planId : "basic-monthly",
    status: subscriptionStatuses.includes(input.status) ? input.status : "active",
    startedAt: asString(input.startedAt || input.createdAt),
    expiresAt: asString(input.expiresAt),
    createdAt: normalizeDate(input.createdAt),
    updatedAt: normalizeDate(input.updatedAt),
    deletedAt: input.deletedAt || null,
  };
}

export function normalizeUserProfile(input = {}) {
  const uid = asString(input.uid || input.userId || input.id);

  return {
    id: uid,
    uid,
    email: input.email ?? null,
    displayName: asString(input.displayName, "Kullanıcı"),
    createdAt: normalizeDate(input.createdAt),
    lastLoginAt: normalizeDate(input.lastLoginAt || input.createdAt),
    role: userRoles.includes(input.role) ? input.role : "customer",
    deletedAt: input.deletedAt || null,
  };
}

export function normalizeSupportRequest(input = {}) {
  return {
    id: asString(input.id),
    name: asString(input.name),
    email: asString(input.email),
    phone: asString(input.phone),
    category: supportRequestCategories.includes(input.category) ? input.category : "other",
    subject: asString(input.subject),
    message: asString(input.message),
    status: supportRequestStatuses.includes(input.status) ? input.status : "open",
    source: asString(input.source, "web"),
    createdAt: normalizeDate(input.createdAt),
    updatedAt: normalizeDate(input.updatedAt),
    deletedAt: input.deletedAt || null,
  };
}

export function normalizeAlert(input = {}) {
  return {
    id: asString(input.id),
    userId: asString(input.userId),
    provinceId: asString(input.provinceId) || null,
    theme: asString(input.theme) || null,
    dateFrom: asString(input.dateFrom),
    dateTo: asString(input.dateTo),
    createdAt: normalizeDate(input.createdAt),
    updatedAt: normalizeDate(input.updatedAt),
    deletedAt: input.deletedAt || null,
  };
}

export function normalizeUserFavorites(input = {}) {
  return {
    id: asString(input.id || input.userId),
    userId: asString(input.userId || input.id),
    provinceIds: uniqueStrings(input.provinceIds),
    dealIds: uniqueStrings(input.dealIds),
    updatedAt: normalizeDate(input.updatedAt),
    deletedAt: input.deletedAt || null,
  };
}

export function normalizePaymentPlan(input = {}) {
  return {
    id: asString(input.id),
    title: asString(input.title),
    priceText: asString(input.priceText),
    period: input.period === "yearly" ? "yearly" : "monthly",
    features: uniqueStrings(input.features),
    createdAt: normalizeDate(input.createdAt),
    updatedAt: normalizeDate(input.updatedAt),
    deletedAt: input.deletedAt || null,
  };
}

export function normalizeTravelFlight(input = {}) {
  return {
    id: asString(input.id),
    airline: asString(input.airline),
    flightCode: asString(input.flightCode),
    cabin: asString(input.cabin, "Ekonomi"),
    departureTime: asString(input.departureTime),
    departureAirport: asString(input.departureAirport),
    arrivalTime: asString(input.arrivalTime),
    arrivalAirport: asString(input.arrivalAirport),
    arrivalNextDay: Boolean(input.arrivalNextDay),
    duration: asString(input.duration),
    stops: asString(input.stops),
    price: Number(input.price) || 0,
    deletedAt: input.deletedAt || null,
  };
}

export function normalizeTravelBus(input = {}) {
  return {
    id: asString(input.id),
    company: asString(input.company),
    departureTime: asString(input.departureTime),
    departureStation: asString(input.departureStation),
    arrivalTime: asString(input.arrivalTime),
    arrivalStation: asString(input.arrivalStation),
    duration: asString(input.duration),
    seatLayout: asString(input.seatLayout, "2+1"),
    price: Number(input.price) || 0,
    cancellable: input.cancellable !== false,
    deletedAt: input.deletedAt || null,
  };
}

export function normalizeTravelHotel(input = {}) {
  return {
    id: asString(input.id),
    name: asString(input.name),
    location: asString(input.location),
    rating: Number(input.rating) || 0,
    ratingLabel: asString(input.ratingLabel),
    reviewCount: Number(input.reviewCount) || 0,
    mealPlan: asString(input.mealPlan),
    badges: uniqueStrings(input.badges),
    campaign: asString(input.campaign),
    price: Number(input.price) || 0,
    discountedPrice: input.discountedPrice != null ? Number(input.discountedPrice) : undefined,
    imageUrl: asString(input.imageUrl),
    deletedAt: input.deletedAt || null,
  };
}

export function normalizeTravelTour(input = {}) {
  return {
    id: asString(input.id),
    title: asString(input.title),
    route: asString(input.route),
    dateRange: asString(input.dateRange),
    duration: asString(input.duration),
    priceEur: Number(input.priceEur) || 0,
    priceTry: Number(input.priceTry) || 0,
    promotion: asString(input.promotion),
    imageUrl: asString(input.imageUrl),
    deletedAt: input.deletedAt || null,
  };
}

export function normalizeTravelCar(input = {}) {
  return {
    id: asString(input.id),
    name: asString(input.name),
    category: asString(input.category),
    supplier: asString(input.supplier),
    transmission: input.transmission === "Manuel" ? "Manuel" : "Otomatik",
    fuel: asString(input.fuel),
    seats: Number(input.seats) || 5,
    bags: Number(input.bags) || 2,
    features: uniqueStrings(input.features),
    pricePerDay: Number(input.pricePerDay) || 0,
    totalPrice: Number(input.totalPrice) || 0,
    freeCancellation: input.freeCancellation !== false,
    imageUrl: asString(input.imageUrl),
    deletedAt: input.deletedAt || null,
  };
}

export function normalizeAppConfig(input = {}) {
  return {
    id: asString(input.id, "mobile"),
    featuredProvinceOrder: uniqueStrings(input.featuredProvinceOrder),
    updatedAt: normalizeDate(input.updatedAt),
  };
}
